/**
 * Auth-flow error code → user-facing message maps.
 *
 * Generic baseline. Pages may compose context-specific overrides:
 *
 *     const INLINE = {
 *       ...AUTH_INLINE_CODE_MESSAGES,
 *       token_expired: "Reset link has expired. Request a new one.",
 *     };
 *
 * Keep messages neutral here; page-side overrides supply the surface-specific
 * wording (e.g., "Reset link..." vs "Verification link...").
 *
 * `step_up_required` belongs in the baseline because its global 403 toast is
 * suppressed in `client.ts`: every page that can receive it needs an inline
 * message of its own, or the failure would surface as a bare generic fallback
 * telling the user to retry later when only re-authenticating will help.
 */

export const AUTH_INLINE_CODE_MESSAGES: Record<string, string> = {
  token_expired: "Link has expired. Request a new one.",
  token_invalid: "Link is invalid or already used. Request a new one.",
  email_already_verified: "Email already verified. You can log in.",
  rate_limited: "Too many requests. Try again in a minute.",
  step_up_required: "Confirm your identity again to continue.",
  // Like `step_up_required`, this one cannot be left to the generic fallback:
  // an SSO-managed account has no password to set, so "please try again" is
  // advice that can never succeed.
  sso_managed:
    "This account signs in through your identity provider, so it has no password to reset. Use the SSO option on the sign-in page.",
};

export const AUTH_FIELD_CODE_MESSAGES: Record<string, string> = {
  password_too_short: "Password is too short.",
  password_too_common: "Password is too common.",
  password_entirely_numeric: "Password cannot be entirely numeric.",
  password_too_similar: "Password is too similar to your personal information.",
};

/**
 * The backend error envelope, `{ "error": { "code", "message", "details" } }`
 * as `core/exceptions.py` builds it, read once.
 *
 * What `details` holds depends on who raised the refusal. A validation refusal
 * carries the code `invalid` (an older backend sent DRF's per-field map of
 * codes there instead; the map is now `field_codes`), its field messages in
 * `details` and a joined sentence in `message`. A refusal the
 * backend named (a plain string code) states its reason in `message`, and
 * whatever it puts in `details` is data for the page, not text for a reader.
 */
export interface ErrorEnvelope {
  /** The machine-readable code when it is a plain string, else `undefined`. */
  code: string | undefined;
  /**
   * `error.message` when it reads as a sentence rather than a stringified
   * dict or list. A `detail` inside the envelope is DRF's key for the same
   * sentence and is read the same way.
   */
  message: string | undefined;
  /**
   * The first message per field in `error.details`, as `{ field: message }`.
   * Values that are neither a string nor a list of strings are skipped.
   */
  fields: Record<string, string>;
  /**
   * Every message in a validation refusal's `details`, nested serializers
   * included, each under its nearest named field (`undefined` for none).
   * Empty unless {@link fieldRefusal}.
   */
  fieldMessages: [field: string | undefined, message: string][];
  /**
   * Whether `details` is a validation refusal's field map, so its messages
   * explain the refusal: no named code, and every leaf a message.
   */
  fieldRefusal: boolean;
  /** `error.details` exactly as sent. */
  details: unknown;
}

/**
 * Codes that mean "the input was invalid": DRF's own `invalid`, and the generic
 * `validation_error`. Any other string code names the refusal.
 */
const VALIDATION_CODES: ReadonlySet<string> = new Set([
  "invalid",
  "validation_error",
]);

function isSentence(value: unknown): value is string {
  if (typeof value !== "string" || value.trim() === "") return false;
  const start = value.trimStart();
  return (
    !start.startsWith("{") &&
    !start.startsWith("[") &&
    !value.includes("ErrorDetail(")
  );
}

/**
 * Every leaf message of a DRF error value (string, list, or field map), each
 * under its nearest named key; list indices are not names. `null` when any
 * leaf is not a string, which makes the value a payload rather than errors.
 */
function errorLeaves(
  value: unknown,
  field?: string,
): [string | undefined, string][] | null {
  if (typeof value === "string") return [[field, value]];
  if (Array.isArray(value) || (value && typeof value === "object")) {
    const out: [string | undefined, string][] = [];
    for (const [key, child] of Object.entries(value as object)) {
      const leaves = errorLeaves(child, /^\d+$/.test(key) ? field : key);
      if (leaves === null) return null;
      out.push(...leaves);
    }
    return out;
  }
  return null;
}

function firstMessages(details: unknown): Record<string, string> {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return {};
  }
  const messages: Record<string, string> = {};
  for (const [field, value] of Object.entries(
    details as Record<string, unknown>,
  )) {
    if (typeof value === "string") {
      messages[field] = value;
    } else if (Array.isArray(value) && typeof value[0] === "string") {
      messages[field] = value[0];
    }
  }
  return messages;
}

/**
 * Every message in a DRF field map, nested serializers included, each under
 * its nearest named field. Empty for anything that is not a field map, or that
 * holds a value that is not a message.
 */
export function fieldMessagesOf(
  value: unknown,
): [field: string | undefined, message: string][] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return (errorLeaves(value) ?? []).filter(([, text]) => text.trim() !== "");
}

/** The envelope of an Axios rejection, or `undefined` when it carries none. */
export function readErrorEnvelope(err: unknown): ErrorEnvelope | undefined {
  const envelope = (
    err as { response?: { data?: { error?: unknown } } } | undefined
  )?.response?.data?.error;
  if (!envelope || typeof envelope !== "object" || Array.isArray(envelope)) {
    return undefined;
  }
  const { code, message, detail, details } = envelope as Record<
    string,
    unknown
  >;
  const named = typeof code === "string" && !VALIDATION_CODES.has(code);
  const fieldMessages = named ? [] : fieldMessagesOf(details);
  return {
    code: typeof code === "string" ? code : undefined,
    message: isSentence(message)
      ? message
      : isSentence(detail)
        ? detail
        : undefined,
    fields: firstMessages(details),
    fieldMessages,
    fieldRefusal: fieldMessages.length > 0,
    details,
  };
}

/**
 * The sentence that explains a refusal: a validation refusal's first field
 * message, otherwise the envelope's own sentence.
 */
export function envelopeReason(envelope: ErrorEnvelope): string | undefined {
  return envelope.fieldMessages[0]?.[1] ?? envelope.message;
}

/**
 * Read the machine-readable error code out of an Axios rejection.
 *
 * Returns `undefined` for anything that is not a plain string code, which
 * covers non-API rejections and the dict-shaped codes DRF emits for field
 * validation errors. Callers fall back to their own generic message.
 */
export function extractErrorCode(err: unknown): string | undefined {
  return readErrorEnvelope(err)?.code;
}

/**
 * The envelope's own sentence, for a refusal that belongs to no field.
 *
 * A 403 or a 404 carries its reason in `error.message` and nothing in
 * `error.details`, so this is what a toast says. Reach for it *after*
 * {@link extractFieldErrors} comes back empty, so a field refusal lands on its
 * field rather than in a toast.
 */
export function extractEnvelopeMessage(err: unknown, fallback: string): string {
  return readErrorEnvelope(err)?.message ?? fallback;
}

/**
 * Field-keyed validation messages from a 400, as `{ field: message }`.
 *
 * DRF's field errors survive `core/exceptions.py` inside `error.details`, one
 * level below where an un-enveloped DRF response would put them. A form that
 * wants to render a refusal on the field that caused it needs the map, which
 * is this.
 *
 * Only the first message per field is kept. A field with several is rare, and a
 * form has one place to put them; the rest stay in the response for the
 * network tab.
 */
export function extractFieldErrors(err: unknown): Record<string, string> {
  return readErrorEnvelope(err)?.fields ?? {};
}

/**
 * Field-keyed refusal codes from a 400, as `{ field: code }`.
 *
 * A validation refusal answers `error.code: "invalid"` and carries DRF's
 * per-field codes in `error.field_codes` (`{"new_password":
 * ["password_too_common"]}`), beside the sentences in `error.details`. A page
 * that has its own wording for a code reads it here and falls back to
 * {@link extractFieldErrors} for a code it has no wording for.
 *
 * A backend from before `field_codes` sent that map as `error.code` itself;
 * it is still read there, so an older server keeps its wording. Empty when
 * neither holds a map.
 */
export function extractFieldCodes(err: unknown): Record<string, string> {
  const error = (
    err as
      | {
          response?: {
            data?: { error?: { code?: unknown; field_codes?: unknown } };
          };
        }
      | undefined
  )?.response?.data?.error;
  const fromFieldCodes = firstMessages(error?.field_codes);
  return Object.keys(fromFieldCodes).length > 0
    ? fromFieldCodes
    : firstMessages(error?.code);
}

/**
 * A whole number the refusal carries in `error.details`, such as
 * `attempts_left` or `retry_after_seconds`. `undefined` when it is absent or
 * not a number, so a page can tell "not said" from zero.
 */
export function detailNumber(err: unknown, key: string): number | undefined {
  const details = readErrorEnvelope(err)?.details;
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return undefined;
  }
  const value = (details as Record<string, unknown>)[key];
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? Math.max(0, n) : undefined;
}

/**
 * Second-factor refusals, shared by the sign-in challenge, recovery-code use
 * and turning two-factor off. Each account gets a fixed number of wrong codes
 * before it locks for a while, so every refusal says how many are left, and a
 * correct code sent too soon after a wrong one is told to wait rather than
 * called wrong.
 */
export function mfaAttemptsLeftMessage(
  attemptsLeft: number,
  lead = "Incorrect code.",
): string {
  if (attemptsLeft <= 0) return `${lead} No attempts left.`;
  return `${lead} ${attemptsLeft} attempt${attemptsLeft === 1 ? "" : "s"} left.`;
}

export function mfaWaitMessage(retryAfterSeconds?: number): string {
  if (!retryAfterSeconds) return "Wait a few seconds and try again.";
  const s = Math.ceil(retryAfterSeconds);
  return `Wait ${s} second${s === 1 ? "" : "s"} and try again.`;
}

/** The lock lasts this long unless the server says otherwise. */
export const MFA_LOCK_MINUTES = 15;

export function mfaLockedMessage(retryAfterSeconds?: number): string {
  const minutes = retryAfterSeconds
    ? Math.max(1, Math.ceil(retryAfterSeconds / 60))
    : MFA_LOCK_MINUTES;
  return `Too many incorrect codes. Try again in ${minutes} minute${minutes === 1 ? "" : "s"}, or reset your password.`;
}

export const MFA_SIGN_IN_COMPLETE_MESSAGE =
  "This sign-in was already completed. Sign in again.";

export const MFA_REQUIRED_FOR_ROLE_MESSAGE =
  "Your role requires two-factor authentication, so it cannot be turned off.";

const GENERIC_ERROR_KEYS = new Set(["__all__", "non_field_errors"]);

export function placeFieldErrors(
  errors: Record<string, string>,
  shown: readonly string[],
): { fields: Record<string, string>; general: string[] } {
  const fields: Record<string, string> = {};
  const specific: string[] = [];
  const generic: string[] = [];
  for (const [key, message] of Object.entries(errors)) {
    if (shown.includes(key)) fields[key] = message;
    else if (GENERIC_ERROR_KEYS.has(key)) generic.push(message);
    else specific.push(message);
  }
  const hasSpecific = specific.length > 0 || Object.keys(fields).length > 0;
  return { fields, general: [...new Set(hasSpecific ? specific : generic)] };
}
